import { fork, call, put, select } from 'redux-saga/effects'
import { takeEvery } from 'redux-saga'
import { push, replace } from 'react-router-redux'

import * as fromService from '@src/redux/modules/service'
import * as fromAuth from '@src/redux/modules/auth'
import * as fromSearch from '@src/redux/modules/search'
import { services, promotionalCode } from '@src/services/api'
import { analytics } from '@src/services/Analytics'
import * as qs from 'utils/querystring'
import * as paths from 'utils/paths'
import * as localStorage from 'utils/localStorage'

// /////////////////////////////////////////////////////////////////////////////
// Sagas
// /////////////////////////////////////////////////////////////////////////////
export function * redeemPromoCode (action) {
  const code = action.payload
  const { estimatedHours, serviceTypeId, aliadaId } = yield select(fromService.getService)

  yield put({type: fromService.REDEEM_PROMO_CODE_STARTED})

  const requestParameters = { code, estimatedHours, serviceTypeId, aliadaId }
  const { response, error } = yield call(promotionalCode.check, requestParameters)

  if (error) {
    yield call(localStorage.removeItem, 'aliada_promoCode')

    return yield put({
      type: fromService.REDEEM_PROMO_CODE_FAILURE,
      payload: error,
      error: true
    })
  }

  yield put({
    type: fromService.REDEEM_PROMO_CODE_SUCCESS,
    payload: response
  })
}

export function * create (action) {
  const service = yield select(fromService.getState)
  const userId = yield select(fromAuth.getCurrentUser)

  const searchQuerystring = yield select(fromSearch.getQuerystring)
  const searchQueryobject = qs.parse(searchQuerystring)
  const { skills, preferences } = searchQueryobject
  const serviceSettings = { skills, preferences }

  const serviceWithSettings = {
    ...service,
    service: {
      ...service.service,
      settings: serviceSettings
    }
  }

  yield put({type: fromService.CREATE_STARTED})

  const { response, error } = yield call(services.create, userId, serviceWithSettings)
  if (error) {
    return yield put({
      type: fromService.CREATE_FAILURE,
      payload: error,
      error: true
    })
  }

  let bgJobResponse
  try {
    bgJobResponse = yield call(services.backgroundJob, response.uuid)
  } catch (e) {
    return yield put({
      type: fromService.CREATE_FAILURE,
      payload: JSON.stringify(e, null, 4),
      error: true
    })
  }

  const serviceCost = yield call(getServiceCost)

  if (bgJobResponse.status === 'error') {
    yield fork([analytics, analytics.serviceConfirmationFailure], {
      userId,
      initialServiceAmount: serviceCost
    })

    return yield put({
      type: fromService.CREATE_FAILURE,
      payload: bgJobResponse.message,
      error: true
    })
  }

  const aliadaId = service.service.aliadaId
  yield fork([analytics, analytics.serviceConfirmationSuccess], {
    userId,
    aliadaId,
    initialServiceAmount: serviceCost
  })

  yield put({
    type: fromService.CREATE_SUCCESS,
    payload: {}
  })

  const { recurrenceId, serviceId } = bgJobResponse

  const successUrl = paths.aliadaReserveSuccess(aliadaId, {
    bookingId: recurrenceId || serviceId,
    serviceTypeId: service.service.serviceTypeId,
    serviceDate: service.service.date,
    serviceTime: service.service.time
  })
  const replaceUrlAction = replace(successUrl)
  yield put(replaceUrlAction)
}

export function * getServiceCost () {
  const aliada = yield select(fromService.getSelectedAliada)
  const promoCode = yield select(fromService.getPromoCode)

  const serviceCost = aliada.serviceCost
  const promoCost = promoCode.discountedPrice

  return promoCost || serviceCost
}

export function * fetch (action) {
  yield put({ type: fromService.FETCH_STARTED })

  const { serviceId } = action.payload
  const userId = yield select(fromAuth.getCurrentUser)

  const { response, error } = yield call(services.fetch, userId, serviceId)

  if (error) {
    yield put({
      type: fromService.FETCH_FAILURE,
      payload: error,
      error: true
    })
    return
  }

  yield put({
    type: fromService.FETCH_SUCCESS,
    payload: response
  })
}

export function * update (action) {
  yield put({ type: fromService.UPDATE_STARTED })

  const { serviceId, serviceChanges } = action.payload
  const userId = yield select(fromAuth.getCurrentUser)
  const { next } = action.meta

  const { response, error } = yield call(services.update, userId, serviceId, serviceChanges)

  if (error) {
    yield put({
      type: fromService.UPDATE_FAILURE,
      payload: error,
      error: true
    })
    return
  }

  yield put({
    type: fromService.UPDATE_SUCCESS,
    payload: response
  })

  yield put(push(next))
}

export function * cancel (action) {
  yield put({ type: fromService.CANCEL_STARTED })

  const userId = yield select(fromAuth.getCurrentUser)
  const { serviceId } = action.payload
  const { next } = action.meta

  const { response, error } = yield call(services.cancel, userId, serviceId)

  if (error) {
    yield put({
      type: fromService.CANCEL_FAILURE,
      payload: error,
      error: true
    })
    return
  }

  yield put({
    type: fromService.CANCEL_SUCCESS,
    payload: response
  })

  yield put(push(next))
}

export function * fetchPenalty (action) {
  yield put({ type: fromService.FETCH_PENALTY_STARTED })

  const userId = yield select(fromAuth.getCurrentUser)
  const { serviceId, serviceChanges, method } = action.payload

  const { response, error } = yield call(services.fetchPenalty, userId, serviceId, serviceChanges, method)

  if (error) {
    yield put({
      type: fromService.FETCH_PENALTY_FAILURE,
      payload: error,
      error: true
    })
    return
  }

  yield put({
    type: fromService.FETCH_PENALTY_SUCCESS,
    payload: response
  })
}

// /////////////////////////////////////////////////////////////////////////////
// Watchers
// /////////////////////////////////////////////////////////////////////////////
export function * watchRedeemPromoCode () {
  yield fork(takeEvery, fromService.REDEEM_PROMO_CODE, redeemPromoCode)
}

export function * watchCreate () {
  yield fork(takeEvery, fromService.CREATE, create)
}

export function * watchFetch () {
  yield fork(takeEvery, fromService.FETCH, fetch)
}

export function * watchUpdate () {
  yield fork(takeEvery, fromService.UPDATE, update)
}

export function * watchCancel () {
  yield fork(takeEvery, fromService.CANCEL, cancel)
}

export function * watchFetchPenalty () {
  yield fork(takeEvery, fromService.FETCH_PENALTY, fetchPenalty)
}
// /////////////////////////////////////////////////////////////////////////////
// Root Saga
// /////////////////////////////////////////////////////////////////////////////
export default function * rootSaga () {
  yield * [
    fork(watchRedeemPromoCode),
    fork(watchCreate),
    fork(watchFetch),
    fork(watchUpdate),
    fork(watchCancel),
    fork(watchFetchPenalty)
  ]
}



// WEBPACK FOOTER //
// ./src/redux/sagas/service.js